import { API } from 'aws-amplify';
import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import { getAuthToken, checkUserGroup, USER_GROUPS } from '../../utils/AuthUtils';
import { Modal, Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { READABLE_SURVEY_INPUT_PROCESSING_STATES } from "../../Constants"
import './styles.scss';
import { getAvailableECSStatuses } from '../../utils/ECSUtils';

const EditTrackingRecordModal = ({ show, selectedRows, onHide }) => {
    const [trackingDataRecords, setTrackingDataRecords] = useState([]);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [hasChanges, setHasChanges] = useState(false);
    const [availableStatuses, setAvailableStatuses] = useState([]);

    const editTrackingRecord = async (companyId, participantId, assignedTo, processingStatus, sensitive, participantName) => {
        try {
            const authToken = await getAuthToken();
            const currentYear = new Date().getFullYear().toString();
            const path = `/survey-questionnaire-submission/records/${currentYear}/${companyId}/${participantId}`;
            const queryStringParameters = {
                assignedTo,
                processingStatus,
                sensitive,
                companyName: participantName
            };

            const result = await API.put('AnnualCompensations', path, {
                headers: {
                    'Token': authToken
                },
                queryStringParameters
            });

            return result;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const handleSave = async () => {
        if (trackingDataRecords.length === 0) {
            return;
        }

        setLoading(true);
        setDisplayError(false);

        try {
            const results = await Promise.all(trackingDataRecords.map(row =>
                editTrackingRecord(
                    row.companyId,
                    row.participantId,
                    row.assignedTo,
                    row.processingStatus,
                    row.sensitive,
                    row.participantName
                )
            ));

            if (results.some(result => !result)) {
                throw new Error("Failed to update one or more records");
            }

            onHide(true); // Pass true to indicate successful save
        } catch (error) {
            setDisplayError(true);
            setErrorMessage(`Failed to update records: ${error.message}`);
            console.error('Error updating records:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeAssignedTo = (index, value) => {
        const newData = [...trackingDataRecords];
        newData[index].assignedTo = value;
        setTrackingDataRecords(newData);
        setHasChanges(true);
    };

    const handleChangeProcessingStatus = (index, value) => {
        const newData = [...trackingDataRecords];
        newData[index].processingStatus = value;
        setTrackingDataRecords(newData);
        setHasChanges(true);
    };

    const handleChangeSensitive = (index, value) => {
        const newData = [...trackingDataRecords];
        newData[index].sensitive = value;
        setTrackingDataRecords(newData);
        setHasChanges(true);
    };

    const getAdminUser = async () => {
        setIsAdminUser(await checkUserGroup(USER_GROUPS.ADMIN));
    };

    const handleClose = () => {
        if (hasChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                closeModal();
            }
        } else {
            closeModal();
        }
    };

    const closeModal = () => {
        setTrackingDataRecords([]);
        setDisplayError(false);
        setErrorMessage("");
        setHasChanges(false);
        onHide(false); // Pass false to indicate no save occurred
    };

    useEffect(() => {
        let isMounted = true;
        if (isMounted && selectedRows) {
            setTrackingDataRecords([...JSON.parse(JSON.stringify(selectedRows))]);
            setDisplayError(false);
            setErrorMessage("");
            setHasChanges(false);
            getAdminUser();

            // Fetch available statuses
            const fetchAvailableStatuses = async () => {
                const statuses = await getAvailableECSStatuses();
                if (isMounted) {
                    setAvailableStatuses(statuses);
                }
            };
            fetchAvailableStatuses();
        }
        return () => { isMounted = false };
    }, [selectedRows, show]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            contentClassName="edit-tracking-records-modal"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Edit {trackingDataRecords.length} Record{trackingDataRecords.length !== 1 ? 's' : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {displayError && (
                    <Alert variant="danger" className="mb-3" onClose={() => setDisplayError(false)} dismissible>
                        {errorMessage}
                    </Alert>
                )}
                <Form>
                    {trackingDataRecords.map((record, index) => (
                        <div key={record.companyId} className="tracking-record-card">
                            <div className="record-header">
                                Record {index + 1} of {trackingDataRecords.length}
                            </div>
                            <Row className="record-content">
                                <Col md={3}>
                                    <Form.Group controlId={`participantName-${index}`}>
                                        <Form.Label>Participant Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={record.participantName}
                                            readOnly
                                            className="readonly-field"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId={`assignedTo-${index}`}>
                                        <Form.Label>
                                            Assigned To{' '}
                                            <span className="required-indicator">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={record.assignedTo}
                                            required
                                            disabled={loading}
                                            onChange={(e) => handleChangeAssignedTo(index, e.target.value)}
                                            placeholder="Enter email address"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group controlId={`processingStatus-${index}`}>
                                        <Form.Label>
                                            Processing Status{' '}
                                            <span className="required-indicator">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            value={record.processingStatus}
                                            disabled={loading}
                                            onChange={(e) => handleChangeProcessingStatus(index, e.target.value)}
                                        >
                                            {availableStatuses.map(([key, value]) => (
                                                <option key={value} value={value}>
                                                    {READABLE_SURVEY_INPUT_PROCESSING_STATES[value]}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group controlId={`sensitive-${index}`}>
                                        <Form.Label>Sensitive</Form.Label>
                                        <div className="d-flex align-items-center mt-2">
                                            <Form.Check
                                                type="checkbox"
                                                checked={record.sensitive}
                                                disabled={loading || !isAdminUser}
                                                onChange={(e) => handleChangeSensitive(index, e.target.checked)}
                                                label={isAdminUser ? undefined : "Admin only"}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!trackingDataRecords.length || loading || !hasChanges}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                aria-hidden="true"
                                className="me-2"
                            />
                            <output className="saving-text" htmlFor="save-button">Saving Changes...</output>
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

EditTrackingRecordModal.propTypes = {
    show: PropTypes.bool.isRequired,
    selectedRows: PropTypes.arrayOf(PropTypes.shape({
        companyId: PropTypes.string.isRequired,
        participantName: PropTypes.string.isRequired,
        assignedTo: PropTypes.string,
        processingStatus: PropTypes.string.isRequired,
        sensitive: PropTypes.bool.isRequired
    })).isRequired,
    onHide: PropTypes.func.isRequired
};

export default EditTrackingRecordModal;